import React, { FC } from 'react';
import { DeviationTooltip as DeviationTooltipType, GateDeviationDataPoint } from '../types';
import { formatTooltipTime } from '../helpers';
import sassVars from 'src/styles/vars.module.scss';
import styled from 'styled-components/macro';

interface DeviationTooltipProps {
  tooltip: DeviationTooltipType;
  dataPoint: GateDeviationDataPoint;
  label: string;
  distanceUnit: string;
  intersectionIndex: number;
  totalIntersections: number;
  translations: {
    gateInterceptsCountOf: string;
    gateDirectionShort: string;
    gateAltitudeShort: string;
  };
  onRemove: () => void;
}

export const DeviationTooltipContainer = styled.div<{ y: number; x: number }>`
  position: absolute;
  top: ${({ y }: { y: number }) => y}px;
  left: ${({ x }: { x: number }) => x}px;
  transform: translate(-50%, calc(-100% - 10px));
  background-color: rgba(255, 255, 255, 1);
  color: black;
  padding: 8px 12px;
  padding-right: 24px;
  border-radius: 4px;
  z-index: 1000;
  pointer-events: auto;
  white-space: nowrap;
  font-size: 12px;
  border: 1px solid ${sassVars.ui03};

  :before {
    content: ' ';
    position: absolute;
    bottom: -4px; /* At the bottom of the tooltip */
    left: 50%;
    margin-left: -4px;
    border-width: 1px;
    border-style: solid;
    border-color: transparent ${sassVars.ui03} ${sassVars.ui03} transparent;
    background-color: rgba(255, 255, 255, 1);
    height: 8px;
    width: 8px;
    transform: rotate(45deg);
  }
`;

export const TooltipTitle = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${sassVars.text01};
  margin-right: 4px;
`;

export const TooltipLabel = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${sassVars.ui04};
  margin-right: 4px;
`;

export const TooltipValue = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${sassVars.text01};
`;

export const TooltipIntersectionCount = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${sassVars.ui04};
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: transparent;
  border: none;
  color: black;
  cursor: pointer;
  padding: 2px 4px;
  font-size: 14px;
  line-height: 1;
  opacity: 0;
  transition: opacity 0.2s;

  ${DeviationTooltipContainer}:hover & {
    opacity: 1 !important;
  }
`;

/**
 * Displays a pinned tooltip with flight information
 * Shows/hides close button on hover
 */
export const DeviationTooltip: FC<DeviationTooltipProps> = ({
  tooltip,
  dataPoint,
  label,
  distanceUnit,
  intersectionIndex,
  totalIntersections,
  translations,
  onRemove,
}) => (
  <DeviationTooltipContainer className="pinned-tooltip" y={tooltip.y} x={tooltip.x}>
    <div>
      <div>
        <TooltipTitle>{label}</TooltipTitle>
        <TooltipIntersectionCount>
          {intersectionIndex} {translations.gateInterceptsCountOf} {totalIntersections}
        </TooltipIntersectionCount>
      </div>
      <div>
        <TooltipLabel>{`${translations.gateAltitudeShort}:`}</TooltipLabel>
        <TooltipValue>
          {Math.round(dataPoint.altitude)} {distanceUnit}
        </TooltipValue>
      </div>
      <div>
        <TooltipLabel>{`${translations.gateDirectionShort}:`}</TooltipLabel>
        <TooltipValue>{dataPoint.direction}</TooltipValue>
      </div>
      <div>
        <TooltipLabel>Time:</TooltipLabel>
        <TooltipValue>{formatTooltipTime(dataPoint.time)}</TooltipValue>
      </div>
    </div>
    <CloseButton onClick={onRemove}>×</CloseButton>
  </DeviationTooltipContainer>
);
